import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { UNSAFE_NavigationContext as NavigationContext } from "react-router-dom";

export default function useUnsavedChangesGuard(dirty) {
  const { navigator } = useContext(NavigationContext);
  const [showExitPrompt, setShowExitPrompt] = useState(false);
  const blockedTransition = useRef(null);
  const unblockRef = useRef(null);

  useEffect(() => {
    if (!dirty || typeof navigator?.block !== "function") return undefined;
    const unblock = navigator.block((transition) => {
      blockedTransition.current = transition;
      setShowExitPrompt(true);
    });
    unblockRef.current = unblock;
    return () => {
      unblock();
      if (unblockRef.current === unblock) unblockRef.current = null;
    };
  }, [dirty, navigator]);

  useEffect(() => {
    if (!dirty) return undefined;
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [dirty]);

  const clearPrompt = useCallback(() => {
    blockedTransition.current = null;
    setShowExitPrompt(false);
  }, []);

  const allowNavigation = useCallback(() => {
    clearPrompt();
    unblockRef.current?.();
    unblockRef.current = null;
  }, [clearPrompt]);

  const exit = useCallback(() => {
    const transition = blockedTransition.current;
    allowNavigation();
    transition?.retry();
  }, [allowNavigation]);

  return { showExitPrompt, stay: clearPrompt, exit, allowNavigation };
}
